import React, {
  useCallback, useEffect, useMemo, useRef, useState,
} from 'react';
import PropTypes from 'prop-types';
import { useHistory } from 'react-router-dom';
import {
  Container,
  Card,
  Row,
  Col,
  Label,
  Input,
  TabContent,
  TabPane,
  Dropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
} from 'reactstrap';
import { DatePicker } from 'antd';
import dayjs from 'dayjs';
import { debounce } from 'lodash';
import { useBoolean } from 'usehooks-ts';

import Breadcrumb from 'components/App/Breadcrumb';
import Meta from 'components/App/Meta';
import Loading from 'components/App/Loading';
import Selection from 'components/App/Selection';
import Pagination from 'components/Pagination';
import DataTableBooking from 'components/App/DataTableBooking';

import { BOOKING_TAB } from 'constants/index';
import { getParamsFormObject } from 'utils';
import cn from 'utils/classnames';

import styles from './ManagerBooking.module.scss';

const breadcrumbList = [
  {
    name: 'Danh sách đặt phòng',
    isActive: true,
    link: '', // TODO: ADD ROUTE WHEN HAVE LINK
  },
];

const NAV_TAB = [
  {
    title: 'Tất cả',
    value: BOOKING_TAB.ALL,
  },
  {
    title: 'Chờ thanh toán',
    value: BOOKING_TAB.PENDING,
  },
  {
    title: 'Đã đặt phòng',
    value: BOOKING_TAB.BOOKED,
  },
  // {
  //   title: 'Đã xác nhận',
  //   value: BOOKING_TAB.APPROVED,
  // },
  {
    title: 'Đã hoàn thành',
    value: BOOKING_TAB.FINISHED,
  },
  {
    title: 'Đã hủy',
    value: BOOKING_TAB.CANCELLED,
  },
  {
    title: 'Đã thay đổi thông tin đặt phòng',
    value: BOOKING_TAB.MODIFIED,
  },
];

const FILTER_BY_DATE = [
  {
    label: 'Ngày đặt phòng',
    value: 'BOOKING',
  },
  {
    label: 'Ngày nhận phòng',
    value: 'CHECK_IN',
  },
  {
    label: 'Ngày trả phòng',
    value: 'CHECK_OUT',
  },
];

const DEFAULT_LIMIT = 20;

function ManagerBooking(props) {
  const {
    loadingBookingList,

    myPlace,
    bookingList,
    bookingTotal,

    actionGetBookingList,
    actionGetMyPlace,
  } = props;

  const tableRef = useRef(null);
  const [pageCount, setPageCount] = useState(0);
  const {
    value: isOpenDropdownFilterByDate,
    toggle: toggleDropdownFilterByDate,
  } = useBoolean(false);

  const history = useHistory();
  const searchQueries = useMemo(() => {
    const params = new URLSearchParams(history.location.search);
    return ({
      page: params.get('page') || 1,
      q: params.get('q') || '',
      limit: DEFAULT_LIMIT,
      dateFilterType: params.get('dateFilterType') || '',
      startDate: params.get('startDate') || '',
      endDate: params.get('endDate') || '',
      statusType: params.get('statusType') || '',
      placeToFilter: params.get('placeToFilter') || '',
    });
  }, [history.location.search]);

  const onChangePage = useCallback((selected) => {
    history.push({
      search: getParamsFormObject({
        ...searchQueries,
        page: selected.selected + 1,
        limit: DEFAULT_LIMIT,
      }),
    });
    tableRef.current.scrollIntoView({ behavior: 'smooth' });
  }, [history, searchQueries]);

  const onSearch = debounce(
    useCallback((e) => history.push({
      search: getParamsFormObject({
        ...searchQueries,
        q: e.target.value,
      }),
    }), [history, searchQueries]),
    500,
  );

  const totalPage = useMemo(() => (Math.ceil((
    bookingTotal || 0) / DEFAULT_LIMIT)), [bookingTotal]);

  // Filter By Date
  const disabledDateCalender = useCallback(
    (current) => current
      && current.valueOf() < Date.parse(searchQueries.startDate), [searchQueries],
  );

  const onSelectPlace = React.useCallback((e) => {
    history.push({
      search: getParamsFormObject({
        ...searchQueries,
        placeToFilter: e.value,
      }),
    });
  }, [history, searchQueries]);

  const onSelectFilterByDate = useCallback((type) => () => {
    history.push({
      search: getParamsFormObject({
        ...searchQueries,
        dateFilterType: type,
      }),
    });
  }, [history, searchQueries]);

  // keyValue variable receives values: startDate, endDate.
  const onSelectDate = useCallback((keyValue) => (_, dateString) => {
    history.push({
      search: getParamsFormObject({
        ...searchQueries,
        [keyValue]: dateString,
      }),
    });
  }, [history, searchQueries]);

  // Filter By Status
  const onSelectFilterByStatus = useCallback((status) => () => {
    history.push({
      search: getParamsFormObject({
        ...searchQueries,
        statusType: status,
      }),
    });
  }, [history, searchQueries]);

  useEffect(() => {
    actionGetMyPlace({ isIntranet: true });
  }, []);

  useEffect(() => {
    setPageCount(totalPage);
  }, [totalPage]);

  useEffect(() => {
    if (searchQueries.dateFilterType && searchQueries.startDate && searchQueries.endDate) {
      actionGetBookingList(searchQueries);
    } else {
      actionGetBookingList({
        page: searchQueries.page,
        q: searchQueries.q,
        limit: searchQueries.limit,
        statusType: searchQueries.statusType,
        placeToFilter: searchQueries.placeToFilter,
      });
    }
  }, [searchQueries]);

  return (
    <div className={`page-content ${styles.Page}`}>
      <Meta name="Danh sách đặt phòng" />

      <Container fluid>
        {/* Render Breadcrumb */}
        <Breadcrumb
          pageTitle="Danh sách đặt phòng"
          breadcrumbList={breadcrumbList}
        />

        <Loading loading={loadingBookingList} loaderOnPage />

        <Card className="Section">
          <Row>
            <Col xxl="6" className="mb-24">
              <Label className="Section__Search__Label">
                Tìm kiếm
              </Label>

              <div className="Section__Search__Container">
                <Input
                  placeholder="Tìm theo ID đơn đặt phòng hoặc Email khách"
                  className={cn('Section__Search__Input')}
                  defaultValue={searchQueries.q}
                  key={searchQueries.q}
                  onChange={onSearch}
                />

                <i className="fas fa-search Section__Search__Icon__Search" />
              </div>
            </Col>

            <Col xxl="6">
              <Label className="Section__Search__Label">
                Bộ lọc
              </Label>

              <Row>
                <Col md="4" className="mb-24">
                  <Dropdown
                    isOpen={isOpenDropdownFilterByDate}
                    toggle={toggleDropdownFilterByDate}
                    className="Section__Search__Dropdown__Wrapper"
                  >
                    <DropdownToggle
                      className="Section__Search__Dropdown"
                      type="button"
                      tag="button"
                    >
                      {FILTER_BY_DATE.find((item) => item.value === searchQueries.dateFilterType)?.label || 'Theo ngày'}
                      <i className="mdi mdi-chevron-down" />
                    </DropdownToggle>

                    <DropdownMenu className="Section__Search__Dropdown__Menu">
                      {FILTER_BY_DATE.map((item) => (
                        <DropdownItem
                          key={item.value}
                          onClick={onSelectFilterByDate(item.value)}
                        >
                          {item.label}
                        </DropdownItem>
                      ))}
                    </DropdownMenu>
                  </Dropdown>
                </Col>

                <Col md="4" className="mb-24">
                  <DatePicker
                    className="Section__Search__DatePicker"
                    onChange={onSelectDate('startDate')}
                    placeholder="Từ ngày"
                    value={searchQueries.startDate ? dayjs(searchQueries.startDate) : null}
                    inputReadOnly
                  />
                </Col>

                <Col md="4" className="mb-24">
                  <DatePicker
                    className="Section__Search__DatePicker"
                    onChange={onSelectDate('endDate')}
                    placeholder="Đến ngày"
                    value={searchQueries.endDate ? dayjs(searchQueries.endDate) : null}
                    inputReadOnly
                    disabledDate={disabledDateCalender}
                  />
                </Col>
              </Row>
            </Col>
          </Row>
          <Row>
            <Col md="4" className="mb-24">
              <Selection
                onChange={onSelectPlace}
                options={myPlace.map((item) => ({
                  label: item.name,
                  value: item._id,
                }))}
                selectedClassName={styles.Selection}
                placeholder="Lọc theo chỗ nghỉ"
                value={searchQueries.placeToFilter}
              />
            </Col>
          </Row>
        </Card>

        <Card className="Section">
          <div className="Tab__Header" ref={tableRef}>
            <div className="Tab__Header__Scroll">
              {NAV_TAB.map((tab) => (
                <div key={tab.value} className="Tab__Header__Tab">
                  <button
                    type="button"
                    onClick={onSelectFilterByStatus(tab.value)}
                    className={`Tab__Header__Tab__Item ${(searchQueries.statusType === tab.value
                      || (tab.value === BOOKING_TAB.ALL && !searchQueries.statusType))
                      && 'Active'}`}
                  >
                    {tab.title}
                  </button>
                </div>
              ))}
            </div>
          </div>

          <TabContent
            activeTab={searchQueries.statusType || BOOKING_TAB.ALL}
            className="Table__Body"
          >
            <Label className="Table__Body__Label">
              {`${bookingTotal || 0} đơn đặt phòng`}
            </Label>

            {NAV_TAB.map((item) => (
              <TabPane
                tabId={item.value}
                key={item.value}
              >
                <DataTableBooking
                  bookings={bookingList}
                  showConfirmColumn={false}
                />
              </TabPane>
            ))}
          </TabContent>
        </Card>

        {bookingTotal !== 0 && (
          <Pagination
            page={+searchQueries.page}
            pageCount={pageCount}
            onChangePage={onChangePage}
          />
        )}
      </Container>
    </div>
  );
}

ManagerBooking.propTypes = {
  loadingBookingList: PropTypes.bool.isRequired,

  bookingTotal: PropTypes.number,
  bookingList: PropTypes.instanceOf(Array),
  myPlace: PropTypes.instanceOf(Array),

  actionGetMyPlace: PropTypes.func.isRequired,
  actionGetBookingList: PropTypes.func.isRequired,
};

ManagerBooking.defaultProps = {
  bookingTotal: 0,
  bookingList: [],
  myPlace: [],
};

export default ManagerBooking;
